import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useSelector, shallowEqual } from "react-redux";
import { validatePublicToken } from "../../services/PublicTokenService";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PublicSessionProvider } from "../../services/context/PublicSessionContext";
import PublicAthleteProfile from "../../components/public-profile/PublicAthleteProfile";

const STATUS = {
  LOADING: "loading",
  VALID: "valid",
  INVALID: "invalid",
};

export default function PublicLink() {
  const { publicToken } = useParams();
  const [status, setStatus] = useState(STATUS.LOADING);
  const [athleteId, setAthleteId] = useState(null);
  const profile = useSelector((state) => state.athletes.profile, shallowEqual);

  useEffect(() => {
    let cancelled = false;

    validatePublicToken(publicToken)
      .then((data) => {
        if (cancelled) return;
        const userId = data?.userId;
        if (userId == null) {
          setStatus(STATUS.INVALID);
          return;
        }
        setAthleteId(userId);
        setStatus(STATUS.VALID);
      })
      .catch(() => {
        if (!cancelled) setStatus(STATUS.INVALID);
      });

    return () => {
      cancelled = true;
    };
  }, [publicToken]);

  if (status === STATUS.LOADING) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (status === STATUS.INVALID) {
    return <InvalidLinkPage />;
  }

  return (
    <PublicSessionProvider token={publicToken} athleteId={athleteId}>
      <div className="min-h-screen bg-[#363636]">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="mb-4 flex justify-center">
            <img
              src="/trackitt-logo.png"
              alt="Trackit Verified"
              className="h-12"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
          {profile?.firstName && (
            <p className="py-6 text-center text-base font-bold uppercase text-gray-100">
              {profile.firstName} {profile.lastName} - live player card
            </p>
          )}
          <PublicAthleteProfile userId={athleteId} />
        </div>
      </div>
    </PublicSessionProvider>
  );
}

function InvalidLinkPage() {
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <div className="bg-white rounded-xl shadow-md p-8 max-w-md w-full text-center">
        <div className="mb-4">
          <svg
            className="mx-auto w-16 h-16 text-red-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-gray-800 mb-2">
          This link is invalid
        </h1>
        <p className="text-gray-600 text-sm mb-6">
          This public link is no longer valid or does not exist. Please speak to
          the person who shared it with you to get a new link.
        </p>
        <Link
          to="/"
          className="inline-block bg-blue-600 text-white px-6 py-2 rounded-md text-sm hover:bg-blue-700 transition"
        >
          Go to Home
        </Link>
      </div>
    </div>
  );
}
